import { type FC, useEffect, useMemo, useState } from 'react';
import type { Order, OrderItem, Address, WalletTransaction, CollectionPoint } from '../../types';
import { useToast } from '../../hooks/useToast';
import { useStores } from '../../hooks/useStores';
import { useOrders } from '../../hooks/useOrders';
import CancelOrderModal from './CancelOrderModal';
import StatusBadge from '../StatusBadge';
import {
    MapPinIcon, UserIcon, PhoneIcon, BuildingStorefrontIcon,
    CalendarIcon, ClockIcon, ShoppingBagIcon, TruckIcon,
} from '@heroicons/react/24/outline';
import OrderTypeBadge from "../OrderTypeBadge.tsx";

interface OrderOverviewTabProps {
    currentOrder: Order;
    onOrderUpdate: () => Promise<void>;
    orderItems?: OrderItem[];
    customerAddresses?: Address[] | null;
    walletTransactions: WalletTransaction[];
    collectionPoint?: CollectionPoint | null;
}

const OrderOverviewTab: FC<OrderOverviewTabProps> = ({currentOrder, onOrderUpdate, orderItems = [], customerAddresses = null, walletTransactions = [], collectionPoint = null,}) => {
    const { success, error } = useToast();
    const { stores, fetchStores } = useStores();
    const { updateOrderStatus } = useOrders();

    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isChatOrder = currentOrder.type?.toLowerCase() === 'chat';
    const isCollectionOrder = currentOrder.delivery_type === 'collection';
    const status = currentOrder.status?.toUpperCase();
    const hideCancel = ['ORDER_CANCELLED', 'DELIVERY_COMPLETE', 'COLLECTION_COMPLETE', 'DELIVERY_FAILED', 'ON_ROUTE'].includes(status);

    useEffect(() => {
        if (stores.length === 0) void fetchStores();
    }, [fetchStores, stores.length]);

    const storeName = useMemo(() => {
        if (!currentOrder?.store_id) return 'No Store Linked';
        if (stores.length === 0) return 'Loading…';
        return stores.find(s => String(s.id) === String(currentOrder.store_id))?.name ?? 'Unknown Store';
    }, [currentOrder.store_id, stores]);

    const handleCancelConfirm = async () => {
        setIsSubmitting(true);
        try {
            await updateOrderStatus(String(currentOrder.id), 'ORDER_CANCELLED');
            await onOrderUpdate();
            success('Order cancelled');
            setIsCancelModalOpen(false);
        } catch (err) {
            error(err instanceof Error ? err.message : 'Failed to cancel order.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const fmt = (cents: number | string) => `R ${(Number(cents) / 100).toFixed(2)}`;

    const walletMath = walletTransactions.reduce((acc, tx) => {
        const amount = Math.abs(Number(tx.amount_cents || 0) / 100);
        if (tx.direction === 'debit') acc.paidFromWallet   += amount;
        else if (tx.direction === 'credit') acc.refundedToWallet += amount;
        return acc;
    }, { paidFromWallet: 0, refundedToWallet: 0 });

    const currentSubtotal = orderItems.reduce((sum, i) => sum + Number(i.total_price || 0), 0) / 100;
    const deliveryFee = Number(currentOrder.delivery_fee || 0) / 100;
    const originalTotal = Number(currentOrder.total || 0) / 100 + deliveryFee;
    const finalCharge = originalTotal - walletMath.paidFromWallet;
    const address = customerAddresses?.[0] ?? null;

    return (
        <div className="space-y-5">

            {/* ── Header: ref + badges + cancel ───────────────────────── */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-xl font-bold text-stone-900 font-mono">{currentOrder.reference}</h2>
                        <StatusBadge status={status} />
                        <OrderTypeBadge type={currentOrder.type} />
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset capitalize ${
                            isCollectionOrder
                                ? 'bg-teal-50 text-teal-700 ring-teal-200'
                                : 'bg-sky-50 text-sky-700 ring-sky-200'
                        }`}>{isCollectionOrder ? '🏪 Collection' : '🚚 Delivery'}</span>
                        {currentOrder.shared_basket_id && (
                            <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset bg-violet-50 text-violet-700 ring-violet-200">
                                🛒 Shared Basket
                            </span>
                        )}
                    </div>
                    <p className="text-[11px] text-stone-400 font-mono">{currentOrder.id}</p>
                </div>
                {!hideCancel && (
                    <button onClick={() => setIsCancelModalOpen(true)}
                            className="self-start inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 transition whitespace-nowrap">
                        Cancel Order
                    </button>
                )}
            </div>

            {/* ── Financial summary cards (ecom orders) ────────────────── */}
            {!isChatOrder && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="rounded-xl border border-stone-200 bg-white p-4">
                        <p className="text-xs text-stone-400 uppercase font-medium tracking-wide">Order Total</p>
                        <p className="mt-1.5 text-xl font-bold text-stone-900">{fmt(currentOrder.total ?? 0)}</p>
                    </div>
                    <div className="rounded-xl border border-stone-200 bg-stone-50 p-4">
                        <p className="text-xs text-stone-400 uppercase font-medium tracking-wide">Wallet Applied</p>
                        <p className="mt-1.5 text-xl font-semibold text-stone-500">
                            {walletMath.paidFromWallet > 0 ? `- R ${walletMath.paidFromWallet.toFixed(2)}` : 'R 0.00'}
                        </p>
                    </div>
                    <div className={`rounded-xl border p-4 ${walletMath.refundedToWallet > 0 ? 'bg-[#f0f7e6] border-[#c5e08a]' : 'bg-white border-stone-200'}`}>
                        <p className={`text-xs uppercase font-medium tracking-wide ${walletMath.refundedToWallet > 0 ? 'text-[#5a7f28]' : 'text-stone-400'}`}>
                            Store Credits
                        </p>
                        <p className={`mt-1.5 text-xl font-bold ${walletMath.refundedToWallet > 0 ? 'text-[#5a7f28]' : 'text-stone-300'}`}>
                            R {walletMath.refundedToWallet.toFixed(2)}
                        </p>
                    </div>
                    <div className="rounded-xl border border-[#ee2b74]/20 bg-pink-50/40 p-4">
                        <p className="text-xs text-[#ee2b74] uppercase font-bold tracking-wide">Final Charge</p>
                        <p className="mt-1.5 text-xl font-bold text-stone-900">R {finalCharge.toFixed(2)}</p>
                    </div>
                </div>
            )}

            {/* ── Chat order summary cards ──────────────────────────────── */}
            {isChatOrder && (
                <div className="grid grid-cols-2  gap-3">
                    <div className="rounded-xl border border-stone-200 bg-white p-4">
                        <p className="text-xs text-stone-400 uppercase font-medium tracking-wide">Parcels</p>
                        <p className="mt-1.5 text-xl font-bold text-stone-900">{currentOrder.parcel_count ?? 0}</p>
                    </div>
                    <div className="rounded-xl border border-stone-200 bg-white p-4">
                        <p className="text-xs text-stone-400 uppercase font-medium tracking-wide">Created</p>
                        <p className="mt-1.5 text-sm font-semibold text-stone-900">
                            {new Date(currentOrder.created_at).toLocaleDateString('en-ZA', {
                                day: 'numeric', month: 'short', year: 'numeric',
                            })}
                        </p>
                    </div>
                </div>
            )}

            {/* ── Customer + Delivery/Collection ────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                {/* Customer */}
                <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                    <div className="flex items-center gap-2 px-5 py-3 bg-stone-50 border-b border-stone-200">
                        <UserIcon className="h-3.5 w-3.5 text-stone-400" />
                        <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-widest">Customer</h3>
                    </div>
                    <div className="p-5">
                        {currentOrder.customer ? (
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-full bg-[#ee2b74]/10 flex items-center justify-center flex-shrink-0">
                                    <span className="text-sm font-bold text-[#ee2b74]">
                                        {(currentOrder.customer.first_name?.[0] ?? '').toUpperCase()}
                                        {(currentOrder.customer.last_name?.[0] ?? '').toUpperCase()}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-stone-900">
                                        {currentOrder.customer.first_name} {currentOrder.customer.last_name}
                                    </p>
                                    {currentOrder.customer.cellphone && (
                                        <div className="flex items-center gap-1 mt-0.5">
                                            <PhoneIcon className="h-3 w-3 text-stone-400" />
                                            <p className="text-xs text-stone-500">{currentOrder.customer.cellphone}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                                <p className="text-xs text-amber-700">Customer details unavailable</p>
                                <p className="text-xs font-mono text-amber-600 mt-0.5">{String(currentOrder.customer_id)}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Delivery / Collection */}
                <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                    <div className="flex items-center gap-2 px-5 py-3 bg-stone-50 border-b border-stone-200">
                        {isCollectionOrder
                            ? <ShoppingBagIcon className="h-3.5 w-3.5 text-stone-400" />
                            : <TruckIcon className="h-3.5 w-3.5 text-stone-400" />}
                        <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-widest">
                            {isCollectionOrder ? 'Collection Point' : 'Delivery Address'}
                        </h3>
                    </div>
                    <div className="p-5">
                        {isCollectionOrder ? (
                            collectionPoint ? (
                                <div className="space-y-1">
                                    <p className="text-sm font-semibold text-stone-900">{collectionPoint.name}</p>
                                    <div className="flex items-start gap-1.5">
                                        <MapPinIcon className="h-3.5 w-3.5 text-stone-400 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs text-stone-600">{collectionPoint.address_line1}</p>
                                            <p className="text-xs text-stone-500">
                                                {[collectionPoint.suburb, collectionPoint.city].filter(Boolean).join(', ')}
                                            </p>
                                        </div>
                                    </div>
                                    {collectionPoint.instructions && (
                                        <div className="mt-2 rounded-lg bg-teal-50 border border-teal-200 px-3 py-2">
                                            <p className="text-xs text-teal-700 italic">{collectionPoint.instructions}</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-stone-400 italic">No collection point selected.</p>
                            )
                        ) : (
                            address ? (
                                <div className="flex items-start gap-1.5">
                                    <MapPinIcon className="h-3.5 w-3.5 text-stone-400 mt-0.5 flex-shrink-0" />
                                    <div className="space-y-0.5">
                                        <p className="text-sm font-semibold text-stone-900">{address.address_1}</p>
                                        {address.address_2 && <p className="text-xs text-stone-600">{address.address_2}</p>}
                                        <p className="text-xs text-stone-500">
                                            {[address.suburb, address.city, address.postcode].filter(Boolean).join(', ')}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-stone-400 italic">
                                    {isChatOrder ? 'No address linked to this customer.' : 'No delivery address on this order.'}
                                </p>
                            )
                        )}
                    </div>
                </div>
            </div>

            {/* ── Order meta ───────────────────────────────────────────── */}
            <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 bg-stone-50 border-b border-stone-200">
                    <BuildingStorefrontIcon className="h-3.5 w-3.5 text-stone-400" />
                    <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-widest">Order Details</h3>
                </div>
                <div className="p-5">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4">
                        <div>
                            <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Store</p>
                            <p className="text-sm font-semibold text-stone-900">{storeName}</p>
                        </div>
                        <div>
                            <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Order Date</p>
                            <div className="flex items-center gap-1.5">
                                <CalendarIcon className="h-3.5 w-3.5 text-stone-400" />
                                <p className="text-sm text-stone-900">
                                    {new Date(currentOrder.created_at).toLocaleDateString('en-ZA', {
                                        day: 'numeric', month: 'short', year: 'numeric',
                                    })}
                                </p>
                            </div>
                        </div>
                        <div>
                            <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Order Type</p>
                            <p className="text-sm font-semibold text-stone-900 capitalize">{currentOrder.type}</p>
                        </div>
                        <div>
                            <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Fulfilment</p>
                            <p className="text-sm font-semibold text-stone-900">
                                {isCollectionOrder ? 'Collection' : 'Delivery'}
                            </p>
                        </div>
                        <div>
                            <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Status</p>
                            <StatusBadge status={status} />
                        </div>
                        {currentOrder.parcel_count !== undefined && currentOrder.parcel_count !== null && (
                            <div>
                                <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Parcels</p>
                                <p className="text-sm font-semibold text-stone-900">{currentOrder.parcel_count}</p>
                            </div>
                        )}
                        {!isChatOrder && (
                            <div>
                                <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Items</p>
                                <p className="text-sm font-semibold text-stone-900">{orderItems.length}</p>
                            </div>
                        )}
                        {currentOrder.delivery_slot_start && (
                            <div>
                                <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">
                                    {isCollectionOrder ? 'Collection Date' : 'Delivery Date'}
                                </p>
                                <div className="flex items-center gap-1.5">
                                    <CalendarIcon className="h-3.5 w-3.5 text-stone-400" />
                                    <p className="text-sm text-stone-900">
                                        {new Date(currentOrder.delivery_slot_start).toLocaleDateString('en-ZA', {
                                            day: 'numeric', month: 'short', year: 'numeric',
                                        })}
                                    </p>
                                </div>
                            </div>
                        )}
                        {currentOrder.delivery_slot_display_sast && (
                            <div>
                                <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Time Window</p>
                                <div className="flex items-center gap-1.5">
                                    <ClockIcon className="h-3.5 w-3.5 text-[#ee2b74]" />
                                    <p className="text-sm font-medium text-[#ee2b74]">{currentOrder.delivery_slot_display_sast}</p>
                                </div>
                            </div>
                        )}
                        {currentOrder.delivery_fee > 0 && (
                            <div>
                                <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Delivery Fee</p>
                                <p className="text-sm text-stone-900">{fmt(currentOrder.delivery_fee)}</p>
                            </div>
                        )}
                        {isCollectionOrder && (
                            <div>
                                <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Collection Confirmation</p>
                                <p className="text-sm font-semibold text-stone-900">Entered pin on collection</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Line items + totals ──────────────────────────────────── */}
            {orderItems.length > 0 && (
                <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                    <div className="flex items-center gap-2 px-5 py-3 bg-stone-50 border-b border-stone-200">
                        <ShoppingBagIcon className="h-3.5 w-3.5 text-stone-400" />
                        <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-widest">Line Items</h3>
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10">
                            <tr>
                                <th className="text-left py-2.5 px-5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Product</th>
                                <th className="text-right py-2.5 px-5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Qty</th>
                                <th className="text-right py-2.5 px-5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Unit</th>
                                <th className="text-right py-2.5 px-5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Total</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                            {orderItems.map(item => (
                                <tr key={item.id} className="hover:bg-stone-50 transition">
                                    <td className="py-3 px-5 text-stone-900 font-medium">{item.product_name}</td>
                                    <td className="text-right py-3 px-5 text-stone-600">{item.quantity}</td>
                                    <td className="text-right py-3 px-5 text-stone-500">{fmt(item.unit_price)}</td>
                                    <td className="text-right py-3 px-5 font-semibold text-stone-900">{fmt(item.total_price)}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="border-t border-stone-200 bg-stone-50 px-5 py-4 space-y-1.5">
                        <div className="flex justify-end gap-6 text-sm text-stone-500">
                            <span>Subtotal</span>
                            <span className="w-28 text-right font-medium text-stone-700">R {currentSubtotal.toFixed(2)}</span>
                        </div>
                        {deliveryFee > 0 && (
                            <div className="flex justify-end gap-6 text-sm text-stone-500">
                                <span>Delivery Fee</span>
                                <span className="w-28 text-right">R {deliveryFee.toFixed(2)}</span>
                            </div>
                        )}
                        {walletMath.paidFromWallet > 0 && (
                            <div className="flex justify-end gap-6 text-sm text-stone-500">
                                <span>Wallet Applied</span>
                                <span className="w-28 text-right text-stone-600">- R {walletMath.paidFromWallet.toFixed(2)}</span>
                            </div>
                        )}
                        {walletMath.refundedToWallet > 0 && (
                            <div className="flex justify-end gap-6 text-sm text-[#8dc63f]">
                                <span>Store Credit Issued</span>
                                <span className="w-28 text-right">R {walletMath.refundedToWallet.toFixed(2)}</span>
                            </div>
                        )}
                        <div className="flex justify-end gap-6 border-t border-stone-200 pt-2 mt-1">
                            <span className="text-sm font-bold text-stone-900">Final Charge</span>
                            <span className="text-lg font-black text-[#ee2b74] w-28 text-right">R {finalCharge.toFixed(2)}</span>
                        </div>
                    </div>
                </div>
            )}

            <CancelOrderModal
                isOpen={isCancelModalOpen}
                orderRef={currentOrder.reference}
                onClose={() => setIsCancelModalOpen(false)}
                onConfirm={handleCancelConfirm}
                isSubmitting={isSubmitting}
            />
        </div>
    );
};

export default OrderOverviewTab;